import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Main, Text, Background } from 'styles/themeColors';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import ChatbotWindow from './ChatbotWindow';

const WidgetButton = styled.button<{ $isOpen: boolean }>`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: ${Main.brandLight};
  border: none;
  box-shadow: 0px 4px 12px rgba(252, 120, 11, 0.3);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0px 6px 16px rgba(252, 120, 11, 0.4);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const UnreadBadge = styled.div<{ $show: boolean }>`
  position: absolute;
  top: -4px;
  right: -4px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: ${Main.error};
  border: 2px solid ${Background.white};
  display: ${props => props.$show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
  color: ${Text.whitePrimary};
`;

const PulseAnimation = styled.span<{ $show: boolean }>`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: ${Main.brandLight};
  opacity: ${props => props.$show ? '0.6' : '0'};
  animation: ${props => props.$show ? 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' : 'none'};
  pointer-events: none;

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
      opacity: 0.6;
    }
    50% {
      transform: scale(1.5);
      opacity: 0;
    }
  }
`;

const ChatbotWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [messages, setMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([]);
  const hasOpenedRef = useRef(false);

  // Show welcome message on first open
  useEffect(() => {
    if (isOpen && !hasOpenedRef.current) {
      hasOpenedRef.current = true;
      setUnreadCount(0);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <>
      <WidgetButton
        $isOpen={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Toggle chatbot"
        title={isOpen ? 'Close chat' : 'Chat with SellerHub Assistant'}
      >
        <PulseAnimation $show={!isOpen && unreadCount > 0} />
        {isOpen ? (
          <CloseIcon htmlColor="white" style={{ fontSize: 28 }} />
        ) : (
          <>
            <UnreadBadge $show={unreadCount > 0}>
              {unreadCount > 9 ? '9+' : unreadCount}
            </UnreadBadge>
            <ChatIcon htmlColor="white" style={{ fontSize: 28 }} />
          </>
        )}
      </WidgetButton>
      {isOpen && (
        <ChatbotWindow
          onClose={handleClose}
          messages={messages}
          setMessages={setMessages}
          setUnreadCount={setUnreadCount}
        />
      )}
    </>
  );
};

export default ChatbotWidget;
